import { type DocumentNode, gql, type QueryResult, useQuery } from '@apollo/client';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { allViewFieldsQuery, sharedViewFieldsQuery } from 'src/dynamoQueries/viewField';

/**
 * GraphQL query object structure for json-to-graphql-query
 */
interface GraphQLQueryObject {
  query: Record<string, unknown>;
}

/**
 * Build view fields query from dynamic query builder
 * @returns DocumentNode for the view fields query
 */
export const buildViewFields = (): DocumentNode => {
  const query = allViewFieldsQuery(true) as GraphQLQueryObject;
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

/**
 * Build shared view fields query from dynamic query builder
 * @returns DocumentNode for the shared view fields query
 */
export const buildSharedViewFields = (): DocumentNode => {
  const query = sharedViewFieldsQuery(true) as GraphQLQueryObject;
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

/**
 * View field node structure from query response
 */
export interface ViewFieldNodeData {
  id: string;
  fieldType: string;
  fieldLabel: string;
  value: string | null;
  metadataFieldId: string | null;
  field: {
    fieldType: string;
    dataType: string;
    highlightIfBlank: boolean;
    isEditable: boolean;
    choiceList: string | null;
    choices: string | null;
  };
}

/**
 * View field edge structure
 */
export interface ViewFieldEdgeData {
  node: ViewFieldNodeData;
}

/**
 * All view fields response structure
 */
export interface AllViewFieldsQueryResponse {
  allViewFields: {
    edges: ViewFieldEdgeData[];
  };
}

/**
 * Variables for query fields with data
 */
export interface QueryFieldsWithDataVariables {
  globalId: string;
  view_ComponentName: string;
}

/**
 * Custom hook for querying view fields with data
 * @param globalId - The global ID of the asset/item
 * @param viewComponentName - The component name for the view
 * @returns Query result containing view fields data
 */
export const useQueryFieldsWithData = (
  globalId: string,
  viewComponentName: string,
): QueryResult<AllViewFieldsQueryResponse, QueryFieldsWithDataVariables> => {
  return useQuery<AllViewFieldsQueryResponse, QueryFieldsWithDataVariables>(buildViewFields(), {
    variables: { globalId, view_ComponentName: viewComponentName },
  });
};
